/**
 * Tab 2: Objectives and Risks
 * 
 * This tab represents the core worksheet for the assessment.
 * Features:
 * - Dynamic table of objective-risk pairings
 * - Link to Control Activities from EX 5
 * - Risk Matrix integration
 * - Financial Statement Assertions
 * 
 * User Story 1 Implementation:
 * - Empty state with "Add Objective" button
 * - Sequential indexing (01, 02, 03...)
 * - Action menu (Add row above/below, Edit, Delete)
 * - Delete confirmation modal
 * - Auto re-indexing on add/delete
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown, message, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../../../store';
import { setObjectives, clearCompliancePurposes } from '../../../../store/slices/riskAssessmentSlice';
import { clearControlActivitiesData } from '../../../../store/slices/controlActivitiesSlice';
import {
  getObjectives,
  createObjective,
  updateObjective,
  deleteObjective,
  parseObjectives,
  denormalizeRiskRanking,
  type Objective,
} from '../../../../services/riskAssessmentService';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from '@syncfusion/ej2-react-grids';
import EditIcon from '../../../../assets/icons/ic-edit.svg?react';
import TrashIcon from '../../../../assets/icons/ic-trash.svg?react';
import ArrowUpIcon from '../../../../assets/icons/ic-arrow-up.svg?react';
import ArrowDownIcon from '../../../../assets/icons/ic-arrow-down.svg?react';
import ConfirmModal from '../../../../components/common/ConfirmModal';
import AddItemButton from '../../../../components/common/AddItemButton';
import ObjectivesItemDrawer, { ObjectivesFormData } from './ObjectivesItemDrawer';
import warningIcon from '../../../../assets/icons/ic-warning.svg';
import RiskMatrixModal from './RiskMatrixModal';

// ==================== STYLED COMPONENTS ====================

const TabContent = styled.div`
  min-height: 400px;
  margin-top: 16px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Empty State Components
const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  margin-bottom: 16px;
  font-size: 15px;
  font-weight: 500;
`;

// Grid Components
const GridWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);  
  overflow: hidden;
  padding: 0 0 0px 2px;

  .e-grid {
    border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    padding: 12px 8px;
    font-size: 12px;
    color: #1F2937;
    white-space: normal;
    word-break: break-word;
    line-height: 1.5;
    vertical-align: top;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  /* Prevent dark hover/active state */
  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const MenuItemText = styled.span<{ $danger?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
`;

const IndexCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

// Chip for Financial Statement Assertions
const AssertionChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  height: 28px;
  background: #FEF9C3;
  color: #854D0E;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 4px;
  margin-bottom: 4px;
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Unranked':
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

const ControlChip = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 4px;
  margin-bottom: 4px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  
  &:hover {
    ${props => props.$clickable && `
      background: #DBEAFE;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(30, 64, 175, 0.15);
    `}
  }
`;

// Tooltip components for multiple assertions
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div<{ $variant?: 'assertion' | 'control' }>`
  padding: 8px 12px;
  background: ${props => props.$variant === 'control' ? '#EFF6FF' : '#FEF9C3'};
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.$variant === 'control' ? '#1E40AF' : '#854D0E'};
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.$variant === 'control' ? '#DBEAFE' : '#FDE68A'};
    transform: translateX(2px);
  }
`;

// ==================== TYPES ====================

// Objective type moved to riskAssessmentService.ts

// ==================== HELPER COMPONENTS ====================


// ==================== MAIN COMPONENT ====================

const Tab2ObjectivesAndRisks: React.FC = () => {
  const { id: assessmentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  // Get objectives from Redux
  const objectives = useSelector((state: RootState) => state.riskAssessment.objectives);
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  
  // State
  const [isLoading, setIsLoading] = useState(false);
  
  // Ref to prevent duplicate fetches
  const hasFetchedObjectivesRef = useRef(false);
  const currentAssessmentIdRef = useRef<string | undefined>(undefined);

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [objectiveToDelete, setObjectiveToDelete] = useState<Objective | null>(null);
  const [isDeletingObjective, setIsDeletingObjective] = useState(false);

  // Drawer states
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedObjectiveForEdit, setSelectedObjectiveForEdit] = useState<Objective | null>(null);
  const [insertObjectiveAtIndex, setInsertObjectiveAtIndex] = useState<number | null>(null);

  // Risk Matrix modal states
  const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);
  const [riskMatrixObjective, setRiskMatrixObjective] = useState<Objective | null>(null);

  // ==================== DATA FETCHING ====================

  /**
   * Fetch objectives from API and store in Redux
   */
  const fetchObjectives = useCallback(async (silent = false) => {
    if (!assessmentId) {
      console.warn('⚠️ Cannot fetch objectives: assessmentId is undefined');
      return;
    }

    if (!silent) {
      setIsLoading(true);
    }

    try {
      // console.log('🔄 Fetching objectives for assessment:', assessmentId);
      const response = await getObjectives(assessmentId, {
        skip: 0,
        take: 1000,
        requiresCounts: true,
      });

      const parsedObjectives = parseObjectives(response.result || []);
      dispatch(setObjectives(parsedObjectives));
      // console.log(`✅ Objectives loaded successfully: ${parsedObjectives.length} items`);
    } catch (error: any) {
      console.error('❌ Failed to fetch objectives:', error);
      if (!silent) {
        message.error(error?.message || 'Failed to load objectives');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [assessmentId, dispatch, selectedReport?.annualReportId]);

  /**
   * Fetch objectives when assessmentId changes (offline first pattern)
   */
  useEffect(() => {
    if (!assessmentId) {
      return;
    }

    // Check if we need to fetch (new assessmentId or first time)
    const isNewAssessment = currentAssessmentIdRef.current !== assessmentId;
    
    if (isNewAssessment) {
      // console.log(`🆕 Objectives: Assessment ID changed: ${currentAssessmentIdRef.current} → ${assessmentId}`);
      hasFetchedObjectivesRef.current = false;
      currentAssessmentIdRef.current = assessmentId;
    }

    // Skip if already fetched for this assessment
    if (hasFetchedObjectivesRef.current) {
      // console.log('⏭️ Skipping duplicate objectives fetch for:', assessmentId);
      return;
    }

    // Mark as fetched before fetching to prevent duplicates
    hasFetchedObjectivesRef.current = true;

    // Fetch objectives
    fetchObjectives();
  }, [assessmentId, fetchObjectives]);

  // ==================== HANDLERS ====================

  const handleAddObjective = () => {
    // console.log('handleAddObjective');
    setDrawerMode('add');
    setSelectedObjectiveForEdit(null);
    setInsertObjectiveAtIndex(null); // Add at end
    setIsDrawerOpen(true);
  };

  const handleAddObjectiveAbove = (objective: Objective) => {
    const objectiveIndex = objectives.findIndex(obj => obj.id === objective.id);
    setDrawerMode('add');
    setSelectedObjectiveForEdit(null);
    setInsertObjectiveAtIndex(objectiveIndex); // Insert at same index (pushes current down)
    setIsDrawerOpen(true);
  };

  const handleAddObjectiveBelow = (objective: Objective) => {
    const objectiveIndex = objectives.findIndex(obj => obj.id === objective.id);
    setDrawerMode('add');
    setSelectedObjectiveForEdit(null);
    setInsertObjectiveAtIndex(objectiveIndex + 1); // Insert after current objective
    setIsDrawerOpen(true);
  };

  const handleEditObjective = (objective: Objective) => {
    setDrawerMode('edit');
    setSelectedObjectiveForEdit(objective);
    setIsDrawerOpen(true);
  };

  const handleDeleteObjective = (objective: Objective) => {
    setObjectiveToDelete(objective);
    setShowDeleteModal(true);
  };

  const handleDeleteObjectiveConfirm = async () => {
    if (!objectiveToDelete) return;

    setIsDeletingObjective(true);
    try {
      // console.log('📤 Deleting objective...');
      await deleteObjective(objectiveToDelete.id);
      
      // Refetch to ensure data consistency and correct order (silent - no loading)
      await fetchObjectives(true);
      
      // Clear compliance purposes cache so Tab 3 will refetch
      // (Compliance purposes may reference the deleted objective)
      dispatch(clearCompliancePurposes());
      
      // Clear control activities cache so EX5 will refetch with updated linked objectives
      dispatch(clearControlActivitiesData());

      message.success('Objective deleted successfully');
      setShowDeleteModal(false);
      setObjectiveToDelete(null);
    } catch (error: any) {
      console.error('❌ Failed to delete objective:', error);
      message.error(error?.message || 'Failed to delete objective');
    } finally {
      setIsDeletingObjective(false);
    }
  };

  const handleDeleteObjectiveCancel = () => {
    setShowDeleteModal(false);
    setObjectiveToDelete(null);
  };

  const handleRiskRankingSelect = async (
    riskLevel: 'High' | 'Med' | 'Low',
    riskRatingSeverityIndex: number,
    riskRatingLikelihoodIndex: number
  ) => {
    if (!assessmentId || !riskMatrixObjective) return;

    // Optimistic update: immediately reflect the new ranking in the UI
    const previousObjectives = [...objectives];
    const updatedObjectives = objectives.map(obj =>
      obj.id === riskMatrixObjective.id
        ? { ...obj, controlRiskRanking: riskLevel, riskRatingSeverityIndex, riskRatingLikelihoodIndex }
        : obj
    );
    dispatch(setObjectives(updatedObjectives));
    setRiskMatrixObjective(null);

    try {
      await updateObjective({
        assessmentId,
        objectives: riskMatrixObjective.objectives,
        risks: riskMatrixObjective.risks,
        riskRating: denormalizeRiskRanking(riskLevel),
        riskRatingSeverityIndex,
        riskRatingLikelihoodIndex,
        controlIds: riskMatrixObjective.controlActivityIds || [],
        controlText: riskMatrixObjective.controlText || null,
        assertionIds: riskMatrixObjective.financialStatementAssertionIds || [],
        objectiveId: riskMatrixObjective.id,
      });

      message.success('Risk ranking updated successfully');
    } catch (error: any) {
      console.error('❌ Failed to update risk ranking:', error);
      message.error(error?.message || 'Failed to update risk ranking');
      // Rollback on failure
      dispatch(setObjectives(previousObjectives));
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedObjectiveForEdit(null);
    setInsertObjectiveAtIndex(null);
  };

  const handleDrawerSubmit = async (formData: ObjectivesFormData) => {
    if (!assessmentId) {
      message.error('Assessment ID is missing');
      return;
    }

    try {
      if (drawerMode === 'add') {
        // Determine target index
        const targetIndex = insertObjectiveAtIndex !== null ? insertObjectiveAtIndex : objectives.length;
        // Call API to create
        // console.log('📤 Creating objective...');
        
        await createObjective({
          assessmentId,
          number: targetIndex + 1,
          objectives: formData.objectives,
          risks: formData.risks,
          riskRating: denormalizeRiskRanking(formData.controlRiskRanking),
          riskRatingSeverityIndex: formData.riskRatingSeverityIndex ?? null,
          riskRatingLikelihoodIndex: formData.riskRatingLikelihoodIndex ?? null,
          controlIds: formData.controlInputMode === 'link' ? formData.controlActivityIds : [],
          controlText: formData.controlInputMode === 'manual' ? formData.controlText : null,
          assertionIds: formData.financialStatementAssertionIds,
        });

        message.success('Objective added successfully');

        // Refetch to get correct IDs and order from backend (silent - no loading)
        await fetchObjectives(true);
        
        // Clear control activities cache so EX5 will refetch with updated linked objectives
        dispatch(clearControlActivitiesData());
        
        // Clear compliance purposes cache so Tab 3 will refetch
        // (Objective IDs may have changed due to re-indexing)
        dispatch(clearCompliancePurposes());
      } else {
        if (!selectedObjectiveForEdit) {
          message.error('Objective not found');
          return;
        }
        // console.log('📤 Updating objective...');
        await updateObjective({
          assessmentId,
          objectives: formData.objectives,
          risks: formData.risks,
          riskRating: denormalizeRiskRanking(formData.controlRiskRanking),
          riskRatingSeverityIndex: formData.riskRatingSeverityIndex ?? null,
          riskRatingLikelihoodIndex: formData.riskRatingLikelihoodIndex ?? null,
          controlIds: formData.controlInputMode === 'link' ? formData.controlActivityIds : [],
          controlText: formData.controlInputMode === 'manual' ? formData.controlText : null,
          assertionIds: formData.financialStatementAssertionIds,
          objectiveId: selectedObjectiveForEdit.id,
        });
        await fetchObjectives(true);
        
        // Clear control activities cache so EX5 will refetch with updated linked objectives
        dispatch(clearControlActivitiesData());
        
        // Clear compliance purposes cache so Tab 3 will refetch with updated objective data
        dispatch(clearCompliancePurposes());
        
        message.success('Objective updated successfully');
      }
    } catch (error: any) {
      console.error('❌ Failed to save objective:', error);

      const defaultErrorMessage = `Failed to ${drawerMode === 'add' ? 'add' : 'update'} objective`;
      message.error(error?.message || defaultErrorMessage);
      throw error;
    }
  };

  // ==================== COLUMN TEMPLATES ====================

  const indexTemplate = (props: Objective) => {
    return <IndexCell>{props.index.toString().padStart(2, '0')}</IndexCell>;
  };

  const objectivesTemplate = (props: Objective) => {
    return (
      <div dangerouslySetInnerHTML={{ __html: props.objectives }} />
    );
  };

  const risksTemplate = (props: Objective) => {
    return (
      <div>{props.risks}</div>
    );
  };

  const riskRankingTemplate = (props: Objective) => {
    if (!isAdmin) {
      return (
        <RiskChip $risk={props.controlRiskRanking} style={{ cursor: 'default' }}>
          {props.controlRiskRanking}
        </RiskChip>
      );
    }
    return (
      <RiskChip
        $risk={props.controlRiskRanking}
        onClick={(e) => {
          e.stopPropagation();
          setRiskMatrixObjective(props);
          setIsRiskMatrixModalOpen(true);
        }}
      >
        {props.controlRiskRanking}
      </RiskChip>
    );
  };

  const assertionsTemplate = (props: Objective) => {
    if (!props.financialStatementAssertions || props.financialStatementAssertions.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>-</span>;
    }

    // Tooltip content for multiple assertions
    const tooltipContent = props.financialStatementAssertions.length > 1 ? (
      <div>
        <TooltipTitle>Assertions</TooltipTitle>
        <TooltipList>
          {props.financialStatementAssertions.map((assertion, index) => (
            <TooltipItem key={index}>
              {assertion}
            </TooltipItem>
          ))}
        </TooltipList>
      </div>
    ) : null;

    const assertionsContent = (
      <div>
        {props.financialStatementAssertions.map((assertion, index) => (
          <AssertionChip key={index}>{assertion}</AssertionChip>
        ))}
      </div>
    );

    // Show tooltip only if multiple assertions
    if (tooltipContent) {
      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {assertionsContent}
        </Tooltip>
      );
    }

    return assertionsContent;
  };

  const handleNavigateToControl = (controlId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/control-activities/${controlId}`);
  };

  const controlsTemplate = (props: Objective) => {
    // Show control codes if available (linked controls)
    if (props.riskMitigatingControls && props.riskMitigatingControls.length > 0) {
      const controlsContent = (
        <div>
          {props.riskMitigatingControls.map((control, index) => {
            const controlId = props.controlActivityIds?.[index];
            return (
              <ControlChip
                key={index}
                $clickable={!!controlId}
                onClick={(e) => controlId && handleNavigateToControl(controlId, e)}
                title={controlId ? 'Click to view control details' : undefined}
              >
                {control}
              </ControlChip>
            );
          })}
        </div>
      );

      // Show tooltip only if multiple controls
      if (props.riskMitigatingControls.length > 1) {
        const tooltipContent = (
          <div>
            <TooltipTitle>Risk Mitigating Controls</TooltipTitle>
            <TooltipList>
              {props.riskMitigatingControls.map((control, index) => {
                const controlId = props.controlActivityIds?.[index];
                return (
                  <TooltipItem
                    key={index}
                    $variant="control"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (controlId) handleNavigateToControl(controlId, e);
                    }}
                    style={{ cursor: controlId ? 'pointer' : 'default' }}
                  >
                    {control}
                  </TooltipItem>
                );
              })}
            </TooltipList>
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            placement="top"
            overlayStyle={{ maxWidth: '400px' }}
            overlayInnerStyle={{
              backgroundColor: '#FFFFFF',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            color="#FFFFFF"
          >
            {controlsContent}
          </Tooltip>
        );
      }

      return controlsContent;
    }
    
    // Show controlText if available (manual text)
    if (props.controlText && props.controlText.trim() !== '') {
      return <div style={{ color: '#1F2937', whiteSpace: 'pre-wrap' }}>{props.controlText}</div>;
    }
    
    return <span style={{ color: '#9CA3AF' }}>-</span>;
  };

  const actionTemplate = (props: Objective) => {
    const getMenuItems = (): MenuProps['items'] => [
      {
        key: 'add-above',
        label: (
          <MenuItemContent>
            <ArrowUpIcon />
            <MenuItemText>Add row above</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleAddObjectiveAbove(props),
      },
      {
        key: 'add-below',
        label: (
          <MenuItemContent>
            <ArrowDownIcon />
            <MenuItemText>Add row below</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleAddObjectiveBelow(props),
      },
      {
        key: 'edit',
        label: (
          <MenuItemContent>
            <EditIcon />
            <MenuItemText>Edit</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleEditObjective(props),
      },
      {
        key: 'delete',
        label: (
          <MenuItemContent>
            <TrashIcon />
            <MenuItemText $danger>Delete row</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleDeleteObjective(props),
      },
    ];

    const handleActionButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <div onClick={handleActionButtonClick}>
        <Dropdown
          menu={{ items: getMenuItems() }}
          trigger={['click']}
          placement="bottomRight"
        >
          <ActionButton>
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      </div>
    );
  };

  // ==================== RENDER ====================

  // Loading state
  if (isLoading) {
    return (
      <TabContent>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: '#6B7280',
          fontSize: '14px'
        }}>
          <LoadingSpinner>
            <div className="spinner" />
          </LoadingSpinner>
        </div>
      </TabContent>
    );
  }

  // Render function for content
  const renderContent = () => {
    if (objectives.length === 0) {
      // Empty state
      return (
        <TabContent>
          <EmptyStateWrapper>
            <EmptyStateText>There are no objectives to display yet.</EmptyStateText>
            <AddItemButton onClick={handleAddObjective} text="Add Objective" disabled={!isAdmin} />
          </EmptyStateWrapper>
        </TabContent>
      );
    }

    // Table with objectives
    return (
      <TabContent>
        <GridWrapper>
          <GridComponent
            key={`objectives-grid-${objectives.length}`}
            dataSource={objectives}
            allowPaging={false}
            enableHover={true}
          >
            <ColumnsDirective>
              <ColumnDirective
                field='index'
                headerText='#'
                width='68'
                textAlign='Center'
                template={indexTemplate}
              />
              <ColumnDirective
                field='objectives'
                headerText='Objectives'
                textAlign='Left'
                template={objectivesTemplate}
              />
              <ColumnDirective
                field='risks'
                headerText='Risks'
                textAlign='Left'
                template={risksTemplate}
              />
              <ColumnDirective
                field='riskMitigatingControls'
                headerText='Risk Mitigating Controls'
                width='160'
                textAlign='Left'
                template={controlsTemplate}
              />
              <ColumnDirective
                field='controlRiskRanking'
                headerText='Risk Rankings'
                width='120'
                textAlign='Left'
                template={riskRankingTemplate}
              />
              <ColumnDirective
                field='financialStatementAssertions'
                headerText='Assertions'
                width='200'
                textAlign='Left'
                template={assertionsTemplate}
              />
              <ColumnDirective
                headerText='Action'
                width='80'
                textAlign='Center'
                template={actionTemplate}
                visible={isAdmin}
              />
            </ColumnsDirective>
            <Inject services={[Page]} />
          </GridComponent>
        </GridWrapper>
      </TabContent>
    );
  };

  // Main render
  return (
    <>
      {renderContent()}

      {/* Objectives Item Drawer */}
      <ObjectivesItemDrawer
        open={isDrawerOpen}
        mode={drawerMode}
        initialValues={selectedObjectiveForEdit ? {
          id: selectedObjectiveForEdit.id,
          objectiveNumber: String(selectedObjectiveForEdit.index).padStart(2, '0'),
          objectives: selectedObjectiveForEdit.objectives,
          risks: selectedObjectiveForEdit.risks,
          controlRiskRanking: selectedObjectiveForEdit.controlRiskRanking,
          riskRatingSeverityIndex: selectedObjectiveForEdit.riskRatingSeverityIndex ?? undefined,
          riskRatingLikelihoodIndex: selectedObjectiveForEdit.riskRatingLikelihoodIndex ?? undefined,
          financialStatementAssertionIds: selectedObjectiveForEdit.financialStatementAssertionIds,
          controlActivityIds: selectedObjectiveForEdit.controlActivityIds,
          controlText: selectedObjectiveForEdit.controlText,
          controlInputMode: selectedObjectiveForEdit.controlInputMode || 'link',
        } : undefined}
        onClose={handleDrawerClose}
        onSubmit={handleDrawerSubmit}
      />

      {/* Delete Objective Confirmation Modal */}
      <ConfirmModal
        open={showDeleteModal}
        onClose={handleDeleteObjectiveCancel}
        onConfirm={handleDeleteObjectiveConfirm}
        icon={warningIcon}
        title="Delete Objective"
        description="Are you sure you want to delete this objective? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={isDeletingObjective}
      />

      {/* Risk Matrix Modal */}
      <RiskMatrixModal
        open={isRiskMatrixModalOpen}
        onClose={() => {
          setIsRiskMatrixModalOpen(false);
          setRiskMatrixObjective(null);
        }}
        onSelect={handleRiskRankingSelect}
        selectedSeverityIndex={riskMatrixObjective?.riskRatingSeverityIndex}
        selectedLikelihoodIndex={riskMatrixObjective?.riskRatingLikelihoodIndex}
      />

    </>
  );
};

export default Tab2ObjectivesAndRisks;
